import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TicketsService } from "@/services";
import { Ticket as TicketType } from "@/types";
import { Usuario } from "@/types";
import toast from "react-hot-toast";
import {
  checkTicketAccess,
  canTransferTicket,
  canCloseTicket,
  canDeleteTicket,
} from "@/utils/ticketPermissions";

interface UseTicketActionsProps {
  ticket: TicketType | null;
  usuario: Usuario | null;
  ticketId: number | undefined;
  onTicketUpdate?: (updatedTicket: Partial<TicketType>) => void;
  onTicketDeleted?: () => void;
  onCloseFecharModal?: () => void;
}

export const useTicketActions = ({
  ticket,
  usuario,
  ticketId,
  onTicketUpdate,
  onTicketDeleted,
  onCloseFecharModal,
}: UseTicketActionsProps) => {
  const navigate = useNavigate();
  const [isFechando, setIsFechando] = useState(false);
  const [isExcluindo, setIsExcluindo] = useState(false);
  const [isTransferindo, setIsTransferindo] = useState(false);

  const handleFecharTicket = useCallback(async () => {
    if (!ticketId || !ticket) return;

    if (!checkTicketAccess(ticket, usuario)) {
      toast.error("Você não tem permissão para fechar este ticket");
      return;
    }

    if (!canCloseTicket(ticket, usuario)) {
      toast.error("Apenas administradores podem fechar tickets");
      return;
    }

    try {
      setIsFechando(true);
      const response = await TicketsService.fecharTicket(ticketId);

      if (response.success) {
        toast.success(response.message);
        if (onTicketUpdate) {
          onTicketUpdate({ status_id: 2, status_nome: "Fechado" });
        }
        // Fechar o modal após sucesso
        if (onCloseFecharModal) {
          onCloseFecharModal();
        }
      } else {
        toast.error("Erro ao fechar ticket");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Erro ao fechar ticket");
    } finally {
      setIsFechando(false);
    }
  }, [ticket, usuario, ticketId, onTicketUpdate, onCloseFecharModal]);

  const handleDelete = useCallback(async () => {
    if (!ticketId || !ticket) return;

    if (!checkTicketAccess(ticket, usuario)) {
      toast.error("Você não tem permissão para excluir este ticket");
      return;
    }

    if (!canDeleteTicket(ticket, usuario)) {
      toast.error("Apenas o criador do ticket pode excluí-lo");
      return;
    }

    try {
      setIsExcluindo(true);
      const response = await TicketsService.deleteTicket(ticketId);

      if (response.success) {
        toast.success(response.message || "Ticket excluído com sucesso");
        if (onTicketDeleted) {
          onTicketDeleted();
        }
        navigate("/");
      } else {
        toast.error("Erro ao excluir ticket");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Erro ao excluir ticket");
    } finally {
      setIsExcluindo(false);
    }
  }, [ticket, usuario, ticketId, navigate, onTicketDeleted]);

  const handleTransferirTicket = useCallback(
    async (departamentoId: number) => {
      if (!ticketId || !ticket || !departamentoId) return;

      if (ticket.status_id === 2 || ticket.status_nome === "Fechado") {
        toast.error("Ticket fechado não pode ser transferido");
        return;
      }

      if (!checkTicketAccess(ticket, usuario)) {
        toast.error("Você não tem permissão para transferir este ticket");
        return;
      }

      if (!canTransferTicket(ticket, usuario)) {
        toast.error("Você não tem permissão para transferir este ticket");
        return;
      }

      try {
        setIsTransferindo(true);
        const response = await TicketsService.transferirTicket(
          ticketId,
          departamentoId
        );

        if (response.success) {
          toast.success(response.message);
          if (onTicketUpdate) {
            onTicketUpdate({
              departamento_id: response.ticket.departamento_id,
              departamento_nome: response.ticket.departamento_nome,
            });
          }
          navigate("/");
        } else {
          toast.error("Erro ao transferir ticket");
        }
      } catch (error: any) {
        toast.error(error.response?.data?.error || error.message || "Erro ao transferir ticket");
      } finally {
        setIsTransferindo(false);
      }
    },
    [ticket, usuario, ticketId, navigate, onTicketUpdate]
  );

  return {
    handleFecharTicket,
    handleDelete,
    handleTransferirTicket,
    isFechando,
    isExcluindo,
    isTransferindo,
  };
};

